import React, { useState } from "react";
import { api, fmtNum, fmtDate, errMsg } from "@finance/lib/api";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { toast } from "sonner";
import { Trash, MinusCircle, PlusCircle, DownloadSimple } from "@phosphor-icons/react";
import { DocNumberField } from "@finance/components/DocNumberField";
import { PrintButton } from "@finance/components/PrintButton";

const MODES = {
  credit_note: { title: "Credit Note — to Client", endpoint: "credit-note", cap: "creditable", priceField: "unit_price", tone: "text-red-700" },
  debit_note: { title: "Debit Note — to Supplier", endpoint: "debit-note", cap: "debitable", priceField: "landed_unit_cost", tone: "text-emerald-700" },
};

function CreateDialog({ mode, tid, summary, onSaved }) {
  const cfg = MODES[mode];
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState({});
  const [scopeCpo, setScopeCpo] = useState("");
  const [meta, setMeta] = useState({ date: new Date().toISOString().slice(0, 10), reason: "", vat_rate: summary?.vat_rate ?? 18 });
  const [saving, setSaving] = useState(false);
  const isCredit = mode === "credit_note";
  const poOptions = React.useMemo(() => {
    if (summary?.client_pos?.length) return summary.client_pos;
    const m = new Map();
    (summary?.line_items || []).forEach((l) => { if (l.client_po_id && !m.has(l.client_po_id)) m.set(l.client_po_id, { id: l.client_po_id, no: l.client_po_no || "—", name: "" }); });
    return [...m.values()];
  }, [summary]);
  const multiCpo = isCredit && poOptions.length > 1;
  const lines = (summary?.line_items || []).filter((l) => l[cfg.cap] > 0.001 && (multiCpo && scopeCpo ? l.client_po_id === scopeCpo : true));

  const save = async () => {
    const items = Object.entries(rows).filter(([, v]) => Number(v.qty) > 0)
      .map(([li, v]) => ({ line_index: Number(li), qty: Number(v.qty), unit_price: Number(v.price) }));
    if (!items.length) { toast.error("Enter a quantity on at least one line"); return; }
    setSaving(true);
    try {
      const payload = { date: meta.date, reason: meta.reason, items, vat_rate: Number(meta.vat_rate) };
      if (meta.doc_no) payload.doc_no = meta.doc_no;
      await api.post(`/transactions/${tid}/${cfg.endpoint}`, payload);
      toast.success(`${cfg.title} recorded & posted`);
      setOpen(false); setRows({}); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" data-testid={`open-${mode}`} onClick={() => { setRows({}); setScopeCpo(isCredit && poOptions.length === 2 ? poOptions[0].id : ""); setOpen(true); }}>{cfg.title}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid={`dialog-${mode}`}>
          <DialogHeader><DialogTitle className={`font-display ${cfg.tone}`}>{cfg.title}</DialogTitle></DialogHeader>
          {multiCpo && (
            <div className="rounded-sm border border-sky-200 bg-sky-50/60 p-3" data-testid="cn-cpo-scope-block">
              <Label className="lx-label text-sky-700">This Somcore PO covers {poOptions.length} Client POs — choose which one this credit note applies to</Label>
              <Select value={scopeCpo || "all"} onValueChange={(v) => setScopeCpo(v === "all" ? "" : v)}>
                <SelectTrigger className="mt-1" data-testid="cn-cpo-scope-select"><SelectValue placeholder="Select Client PO" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Client POs</SelectItem>
                  {poOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.no}{p.name ? ` · ${p.name}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="lx-label">Date</Label><DateField className="mt-1" value={meta.date} onChange={(e) => setMeta({ ...meta, date: e.target.value })} /></div>
            <div><Label className="lx-label">VAT %</Label><Input type="number" className="mt-1" value={meta.vat_rate} onChange={(e) => setMeta({ ...meta, vat_rate: e.target.value })} /></div>
            <div><Label className="lx-label">Reason</Label><Input className="mt-1" data-testid={`${mode}-reason`} value={meta.reason} onChange={(e) => setMeta({ ...meta, reason: e.target.value })} /></div>
            <div className="col-span-3"><DocNumberField docKey={mode} label="Document No" testid={`${mode}-doc-no`} value={meta.doc_no} onChange={(v) => setMeta({ ...meta, doc_no: v })} /></div>
          </div>
          <div className="mt-2 max-h-72 overflow-auto rounded-sm border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50"><tr className="lx-label">
                <th className="px-3 py-1.5 text-left">Item</th>
                <th className="px-3 py-1.5 text-right">Available</th>
                <th className="px-3 py-1.5 text-right">Qty</th>
                <th className="px-3 py-1.5 text-right">Unit price</th>
              </tr></thead>
              <tbody>
                {lines.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400">Nothing available — invoice the order first.</td></tr>}
                {lines.map((l) => (
                  <tr key={l.line_index} className="border-t border-gray-100">
                    <td className="px-3 py-1.5">{l.description}</td>
                    <td className="px-3 py-1.5 text-right lx-num">{fmtNum(l[cfg.cap])}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Input type="number" className="ml-auto w-24 text-right" data-testid={`${mode}-qty-${l.line_index}`}
                        value={rows[l.line_index]?.qty || ""} onChange={(e) => setRows({ ...rows, [l.line_index]: { ...rows[l.line_index], qty: e.target.value } })} />
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <Input type="number" className="ml-auto w-28 text-right"
                        value={rows[l.line_index]?.price ?? (l[cfg.priceField] || l.unit_price || "")}
                        onChange={(e) => setRows({ ...rows, [l.line_index]: { ...rows[l.line_index], price: e.target.value } })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" disabled={saving} data-testid={`${mode}-save`} onClick={save}>{saving ? "Saving…" : "Record & Post"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function NoteList({ title, rows, icon: Icon, onDelete, kind, pdfPrefix }) {
  return (
    <div>
      <h3 className="lx-label mb-2 flex items-center gap-1 text-gray-500"><Icon size={14} />{title} ({rows.length})</h3>
      <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <tbody>
            {rows.length === 0 && <tr><td className="px-3 py-3 text-center text-gray-400">None.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`doc-${r.note_no}`} className="border-b border-gray-100">
                <td className="px-3 py-2 font-semibold">{r.note_no}</td>
                <td className="px-3 py-2 text-gray-500">{fmtDate(r.date)}</td>
                <td className="px-3 py-2 text-gray-500">{(r.items || []).reduce((s, i) => s + (i.qty || 0), 0)} units{r.total_amount != null ? ` · ${fmtNum(r.total_amount)}` : ""}</td>
                <td className="px-3 py-2 text-gray-400">{r.reason}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <PrintButton path={`pdf/${pdfPrefix}/${r.id}`} testid={`dl-note-${r.note_no}`} className="text-gray-400 hover:text-[#1B4383]"><DownloadSimple size={16} /></PrintButton>
                    <Button size="icon" variant="ghost" data-testid={`del-${r.note_no}`} onClick={() => onDelete(kind, r.id)}><Trash size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CreditDebitNotes({ tid, summary, onChanged }) {
  const del = async (kind, id) => {
    if (!window.confirm("Delete this note and reverse its posting?")) return;
    const url = kind === "credit" ? `/credit-notes/${id}` : `/debit-notes/${id}`;
    try { await api.delete(url); toast.success("Deleted & reversed"); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-6" data-testid="credit-debit-notes">
      <div className="mb-3 flex flex-wrap gap-2">
        <CreateDialog mode="credit_note" tid={tid} summary={summary} onSaved={onChanged} />
        <CreateDialog mode="debit_note" tid={tid} summary={summary} onSaved={onChanged} />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <NoteList title="Credit Notes (to Client)" rows={summary?.credit_notes || []} icon={MinusCircle} onDelete={del} kind="credit" pdfPrefix="credit-note" />
        <NoteList title="Debit Notes (to Supplier)" rows={summary?.debit_notes || []} icon={PlusCircle} onDelete={del} kind="debit" pdfPrefix="debit-note" />
      </div>
    </div>
  );
}
